import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import StatusBadge from '../../components/StatusBadge';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const DURUM_OPTIONS = ['ODENECEK', 'KISMI_ODENDI', 'ODENDI', 'IPTAL'];

const FaturalarPage = () => {
  useAuth();

  const [faturalar, setFaturalar] = useState([]);
  const [tedarikciler, setTedarikçiler] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtre state
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });
  const [durum, setDurum] = useState('');
  const [tedarikçiId, setTedarikciId] = useState('');
  const [baslangicTarihi, setBaslangicTarihi] = useState('');
  const [bitisTarihi, setBitisTarihi] = useState('');
  const [minTutar, setMinTutar] = useState('');
  const [maxTutar, setMaxTutar] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Tedarikçi listesi
  useEffect(() => {
    fetch(`${API_URL}/api/tedarikciler`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => setTedarikçiler(Array.isArray(data) ? data : []))
      .catch(() => setTedarikçiler([]));
  }, []);

  const loadFaturalar = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (arama.trim()) params.append('arama', arama.trim());
    if (durum) params.append('durum', durum);
    if (tedarikçiId) params.append('tedarikci_id', tedarikçiId);
    if (baslangicTarihi) params.append('baslangic_tarihi', baslangicTarihi);
    if (bitisTarihi) params.append('bitis_tarihi', bitisTarihi);
    if (minTutar) params.append('min_tutar', minTutar);
    if (maxTutar) params.append('max_tutar', maxTutar);

    const qs = params.toString();
    const url = `${API_URL}/api/faturalar${qs ? '?' + qs : ''}`;

    fetch(url, { headers: getAuthHeaders() })
      .then(res => {
        if (res.status === 401) { window.location.href = '/login'; return []; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setFaturalar(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        logError('Faturalar yüklenemedi:', err);
        setFaturalar([]);
        setLoading(false);
      });
  }, [arama, durum, tedarikçiId, baslangicTarihi, bitisTarihi, minTutar, maxTutar]);

  // Ilk yükleme
  useEffect(() => { loadFaturalar(); }, [loadFaturalar]);

  const handleFiltrele = (e) => {
    e.preventDefault();
    loadFaturalar();
  };

  const handleTemizle = () => {
    setArama('');
    setDurum('');
    setTedarikciId('');
    setBaslangicTarihi('');
    setBitisTarihi('');
    setMinTutar('');
    setMaxTutar('');
  };

  const aktifFiltreSayisi = [arama, durum, tedarikçiId, baslangicTarihi, bitisTarihi, minTutar, maxTutar]
    .filter(v => v).length;

  const getTedarikciUnvan = (fatura) => {
    if (fatura?.tedarikci_unvan) return fatura.tedarikci_unvan;
    if (fatura?.tedarikci) return fatura.tedarikci;
    if (fatura?.tedarikçi) return fatura.tedarikçi;

    const tedarikci = tedarikciler.find(
      t => Number(t.id) === Number(fatura?.tedarikci_id)
    );

    return tedarikci?.unvan || '';
  };

  return (
    <div>
      <Header title="Faturalar" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Arama + Filtre Toggle */}
        <div style={{
          background: colors.surface, borderRadius: radius.md, padding: '16px',
          marginBottom: '16px', boxShadow: shadows.sm
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={arama}
              onChange={e => setArama(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadFaturalar()}
              placeholder="Fatura no veya tedarikçi ara..."
              style={{
                flex: 1, padding: '10px 14px', border: `1px solid ${colors.border}`,
                borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box'
              }}
            />
            <button onClick={loadFaturalar} style={{
              background: styles.primary, color: 'white', border: 'none',
              borderRadius: radius.sm, padding: '10px 20px', cursor: 'pointer',
              fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap'
            }}>Ara</button>
            <button onClick={() => setShowFilters(!showFilters)} style={{
              background: showFilters ? styles.secondary : '#f8f9fa',
              color: showFilters ? 'white' : '#333',
              border: showFilters ? 'none' : `1px solid ${colors.border}`,
              borderRadius: radius.sm, padding: '10px 16px', cursor: 'pointer',
              fontSize: '13px', whiteSpace: 'nowrap', position: 'relative'
            }}>
              Filtreler
              {aktifFiltreSayisi > 0 && (
                <span style={{
                  position: 'absolute', top: '-6px', right: '-6px',
                  background: '#dc3545', color: 'white', borderRadius: '50%',
                  width: '18px', height: '18px', fontSize: '11px', fontWeight: 'bold',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>{aktifFiltreSayisi}</span>
              )}
            </button>
          </div>

          {/* Gelismis Filtreler */}
          {showFilters && (
            <form onSubmit={handleFiltrele} style={{
              marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${colors.border}`
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={filterLabelStyle}>Durum</label>
                  <select value={durum} onChange={e => setDurum(e.target.value)} style={filterInputStyle}>
                    <option value="">Tumu</option>
                    {DURUM_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={filterLabelStyle}>Tedarikçi</label>
                  <select value={tedarikçiId} onChange={e => setTedarikciId(e.target.value)} style={filterInputStyle}>
                    <option value="">Tumu</option>
                    {tedarikciler.map(t => <option key={t.id} value={t.id}>{t.unvan}</option>)}
                  </select>
                </div>
                <div>
                  <label style={filterLabelStyle}>Baslangic Tarihi</label>
                  <input type="date" value={baslangicTarihi} onChange={e => setBaslangicTarihi(e.target.value)} style={filterInputStyle} />
                </div>
                <div>
                  <label style={filterLabelStyle}>Bitis Tarihi</label>
                  <input type="date" value={bitisTarihi} onChange={e => setBitisTarihi(e.target.value)} style={filterInputStyle} />
                </div>
                <div>
                  <label style={filterLabelStyle}>Min Tutar</label>
                  <input type="number" step="0.01" min="0" value={minTutar} onChange={e => setMinTutar(e.target.value)} placeholder="0.00" style={filterInputStyle} />
                </div>
                <div>
                  <label style={filterLabelStyle}>Max Tutar</label>
                  <input type="number" step="0.01" min="0" value={maxTutar} onChange={e => setMaxTutar(e.target.value)} placeholder="0.00" style={filterInputStyle} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="button" onClick={handleTemizle} style={{
                  background: colors.backgroundAlt, color: '#333', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, padding: '8px 16px', cursor: 'pointer', fontSize: '13px'
                }}>Temizle</button>
                <button type="submit" style={{
                  background: styles.primary, color: 'white', border: 'none',
                  borderRadius: radius.sm, padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold'
                }}>Filtrele</button>
              </div>
            </form>
          )}
        </div>

        {/* Sonuc Sayısı */}
        {!loading && (
          <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '8px' }}>
            {faturalar.length} fatura bulundu
            {aktifFiltreSayisi > 0 && (
              <button onClick={handleTemizle} style={{
                background: 'none', border: 'none', color: styles.primary,
                cursor: 'pointer', fontSize: '13px', marginLeft: '8px', textDecoration: 'underline'
              }}>Filtreleri temizle</button>
            )}
          </div>
        )}

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden', boxShadow: shadows.sm }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: styles.secondary, color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left' }}>Fatura No</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Tedarikçi</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Tutar</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>KDV</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Toplam</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Vade</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center' }}>Yükleniyor...</td></tr>
              ) : faturalar.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary }}>Fatura bulunamadı.</td></tr>
              ) : faturalar.map(f => (
                <tr key={f.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '15px', fontWeight: 'bold' }}>{f.fatura_no}</td>
                  <td style={{ padding: '15px' }}>{getTedarikciUnvan(f)}</td>
                  <td style={{ padding: '15px', textAlign: 'right' }}>{f.tutar?.toLocaleString()}</td>
                  <td style={{ padding: '15px', textAlign: 'right' }}>{f.kdv?.toLocaleString()}</td>
                  <td style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold' }}>{f.toplam?.toLocaleString()}</td>
                  <td style={{ padding: '15px' }}>{f.vade}</td>
                  <td style={{ padding: '15px' }}><StatusBadge status={f.durum} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const filterLabelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold', color: '#555' };
const filterInputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box'
};

export default FaturalarPage;
